import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Pagination, Button, Card, Row, Col, Badge, Container } from 'react-bootstrap';
import { FaSearch, FaTrash, FaEye, FaMapMarkerAlt, FaClock, FaStar, FaFilter, FaLanguage, FaSearchPlus, FaTimes, FaSync, FaUsers } from 'react-icons/fa';
import Swal from 'sweetalert2';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import HeaderTopSection from "../../components/partials/HeaderTop";
import Banner from "../../components/partials/Banner";
import ProfessionalDetailModal from "../../components/modals/professional/ProfessionalDetailModal";
import ProfessionalService from '../../services/ProfessionalService';
import AuthService from '../../services/AuthService';
import LoadingSpinner from '../../utils/LoadingSpinner';
import '../../assets/css/ProfessionalManagement.css';

// Hook personnalisé pour le debounce
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};


const DEFAULT_AVATAR =
    "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300' viewBox='0 0 300 300'%3E%3Crect width='300' height='300' fill='%23e9ecef'/%3E%3Ccircle cx='150' cy='115' r='55' fill='%23ced4da'/%3E%3Cellipse cx='150' cy='265' rx='95' ry='75' fill='%23ced4da'/%3E%3C/svg%3E";
const CARD_IMAGE_HEIGHT = 220;

// Liste des statuts valides
const VALID_STATUSES = ['ACCEPTED', 'REJECTED', 'PENDING'];

// Fonction utilitaire pour parser les dates au format "DD-MM-YYYY HH:mm"
const parseModifiedDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const parts = dateStr.trim().split(' ');
    if (parts.length === 0) return null;

    const [day, month, year] = parts[0].split('-').map(Number);
    let hours = 0, minutes = 0;

    if (parts[1]) {
        const timeParts = parts[1].split(':').map(Number);
        hours = timeParts[0] || 0;
        minutes = timeParts[1] || 0;
    }

    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day, hours, minutes);
};

const ProfessionalManagementPage = () => {
    const [professionals, setProfessionals] = useState([]);
    const [allProfessionals, setAllProfessionals] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilter, setActiveFilter] = useState('pending');
    const [selectedProfessional, setSelectedProfessional] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(true);
    const [error, setError] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSlides, setLightboxSlides] = useState([]);
    const [showClearSearch, setShowClearSearch] = useState(false);

    // ✅ NOUVEAU : Référence pour le scroll
    const listTopRef = useRef(null);

    // Modification 1 : Passer de 6 à 12 éléments par page
    const itemsPerPage = 12;

    const filters = [
        { key: 'all', label: 'Tous les professionnels' },
        { key: 'accepted', label: 'Validés' },
        { key: 'pending', label: 'En attente' },
        { key: 'rejected', label: 'Rejetés' },
        { key: 'no_identification', label: 'Sans identification' }
    ];

    useEffect(() => {
        fetchProfessionals();
    }, []);

    useEffect(() => {
        if (debouncedSearchTerm || activeFilter !== 'all') {
            setCurrentPage(1);
        }
    }, [debouncedSearchTerm, activeFilter]);

    // ✅ NOUVEAU : UseEffect pour faire défiler la page vers le haut lors du changement de page
    useEffect(() => {
        if (listTopRef.current) {
            listTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [currentPage]);

    const resolveProfilePhoto = (professional) => {
        return (
            professional.profilePhotoUrlPresigned ||
            professional.profilePhotoUrl ||
            professional.user?.profilePictureUrl ||
            DEFAULT_AVATAR
        );
    };

    const fetchProfessionals = async () => {
        const startTime = Date.now();
        setShowLoadingSpinner(true);
        setIsLoading(true);
        setError(null);

        try {
            const response = await ProfessionalService.getAllProfessionals();
            const formattedProfessionals = response.map(professional => ({
                ...professional,
                displayPhotoUrl: resolveProfilePhoto(professional),
            }));

            setProfessionals(formattedProfessionals);
            setAllProfessionals(formattedProfessionals);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                setIsLoading(false);
            }, remainingTime);

        } catch (err) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                console.error('Erreur lors du chargement des professionnels:', err);
                setError(err.message || 'Erreur lors du chargement des professionnels');
                setShowLoadingSpinner(false);
                setIsLoading(false);
                Swal.fire('Erreur !', 'Impossible de charger les professionnels. Veuillez réessayer.', 'error');
            }, remainingTime);
        }
    };

    const filteredProfessionals = (debouncedSearchTerm || activeFilter !== 'all' ? allProfessionals : professionals).filter(professional => {
        const searchLower = debouncedSearchTerm.toLowerCase();
        const fullName = `${professional.user.firstName} ${professional.user.lastName}`.toLowerCase();
        const email = professional.user.email.toLowerCase();
        const service = professional.selectedService?.toLowerCase();
        const city = professional.user.city?.toLowerCase();

        const searchMatch = !debouncedSearchTerm ||
            fullName.includes(searchLower) ||
            email.includes(searchLower) ||
            service?.includes(searchLower) ||
            city?.includes(searchLower);

        let filterMatch = true;
        switch (activeFilter) {
            case 'accepted':
                filterMatch = professional.status === 'ACCEPTED';
                break;
            case 'pending':
                filterMatch = professional.status === 'PENDING';
                break;
            case 'rejected':
                filterMatch = professional.status === 'REJECTED';
                break;
            case 'no_identification':
                // Si le statut est absent, null ou différent des 3 statuts valides
                filterMatch = !VALID_STATUSES.includes(professional.status);
                break;
            default:
                filterMatch = true;
        }

        return searchMatch && filterMatch;
    });

    // Modification 3 : Tri par date de modification (les plus récents en premier, sans date en dernier)
    const sortedFilteredProfessionals = [...filteredProfessionals].sort((a, b) => {
        const dateA = parseModifiedDate(a.modifiedAt);
        const dateB = parseModifiedDate(b.modifiedAt);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB.getTime() - dateA.getTime();
    });

    const totalPages = Math.ceil(sortedFilteredProfessionals.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentProfessionals = sortedFilteredProfessionals.slice(startIndex, startIndex + itemsPerPage);

    const getFilterStats = useCallback(() => {
        return {
            all: allProfessionals.length,
            accepted: allProfessionals.filter(p => p.status === 'ACCEPTED').length,
            pending: allProfessionals.filter(p => p.status === 'PENDING').length,
            rejected: allProfessionals.filter(p => p.status === 'REJECTED').length,
            // Compte tous ceux dont le statut n'est pas valide
            no_identification: allProfessionals.filter(p => !VALID_STATUSES.includes(p.status)).length,
        };
    }, [allProfessionals]);

    const filterStats = getFilterStats();

    const getServiceLabel = (service) => {
        const services = {
            'garde_enfant': 'Garde d\'enfants',
            'reparation_electromenager': 'Réparation Électroménager',
            'cuisine_a_domicile': 'Cuisine à Domicile',
            'jardinage': 'Jardinage'
        };
        return services[service] || service;
    };

    const getStatusBadge = (professional) => {
        switch (professional.status) {
            case 'ACCEPTED':
                return <Badge className="status-badge status-accepted">Validé</Badge>;
            case 'PENDING':
                return <Badge className="status-badge status-pending">En attente</Badge>;
            case 'REJECTED':
                return <Badge className="status-badge status-rejected">Rejeté</Badge>;
            default:
                // Si null, undefined ou autre, on affiche "Sans identification"
                return <Badge className="status-badge bg-dark text-white">Sans identification</Badge>;
        }
    };

    const handleViewDetails = (professional) => {
        setSelectedProfessional(professional);
        setIsModalOpen(true);
    };

    const handleOpenPhotoPreview = (url) => {
        setLightboxSlides([{ src: url }]);
        setLightboxOpen(true);
    };

    const handleDelete = async (professionalId) => {
        const result = await Swal.fire({
            title: 'Confirmer la suppression',
            html: `Êtes-vous sûr de vouloir supprimer ce professionnel ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            const startTime = Date.now();
            setShowLoadingSpinner(true);

            try {
                await ProfessionalService.deleteProfessional(professionalId);
                setProfessionals(prev => prev.filter(p => p.id !== professionalId));
                setAllProfessionals(prev => prev.filter(p => p.id !== professionalId));

                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(3000 - elapsedTime, 0);

                setTimeout(() => {
                    setShowLoadingSpinner(false);
                    Swal.fire('Supprimé !', 'Le professionnel a été supprimé avec succès.', 'success');
                }, remainingTime);
            } catch (err) {
                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(3000 - elapsedTime, 0);

                setTimeout(() => {
                    console.error('Erreur lors de la suppression:', err);
                    setShowLoadingSpinner(false);
                    Swal.fire('Erreur !', 'La suppression a échoué. Veuillez réessayer.', 'error');
                }, remainingTime);
            }
        }
    };

    const getCurrentFormattedDate = () => {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    const handleValidateProfessional = async (professionalId) => {
        const startTime = Date.now();
        setShowLoadingSpinner(true);

        try {
            const currentUser = AuthService.getCurrentUser();
            const updateData = {
                userValidated: currentUser?.username || 'admin',
                status: 'ACCEPTED',
                validationDate: getCurrentFormattedDate(),
                lastModifiedBy: currentUser?.username || 'admin'
            };

            await ProfessionalService.updateProfessional(professionalId, updateData);

            setProfessionals(prev => prev.map(p =>
                p.id === professionalId ? { ...p, ...updateData } : p
            ));
            setAllProfessionals(prev => prev.map(p =>
                p.id === professionalId ? { ...p, ...updateData } : p
            ));

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                Swal.fire('Validé !', 'Le professionnel a été validé avec succès.', 'success');
            }, remainingTime);
        } catch (err) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                console.error('Erreur lors de la validation:', err);
                setShowLoadingSpinner(false);
                Swal.fire('Erreur !', 'La validation a échoué. Veuillez réessayer.', 'error');
            }, remainingTime);
        }
    };

    const handleRejectProfessional = async (professionalId, rejectionReason) => {
        const startTime = Date.now();
        setShowLoadingSpinner(true);

        try {
            const currentUser = AuthService.getCurrentUser();
            const updateData = {
                userRejected: currentUser?.username || 'admin',
                status: 'REJECTED',
                rejectionReason: rejectionReason,
                lastModifiedBy: currentUser?.username || 'admin'
            };

            await ProfessionalService.updateProfessional(professionalId, updateData);

            setProfessionals(prev => prev.map(p =>
                p.id === professionalId ? { ...p, ...updateData } : p
            ));
            setAllProfessionals(prev => prev.map(p =>
                p.id === professionalId ? { ...p, ...updateData } : p
            ));

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                Swal.fire('Rejeté !', 'Le professionnel a été rejeté.', 'success');
            }, remainingTime);
        } catch (err) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                console.error('Erreur lors du rejet:', err);
                setShowLoadingSpinner(false);
                Swal.fire('Erreur !', 'Le rejet a échoué. Veuillez réessayer.', 'error');
            }, remainingTime);
        }
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Gestion des Professionnels"
                backgroundImage="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
            />

            <LoadingSpinner
                isVisible={showLoadingSpinner}
                loadingText="Chargement des professionnels..."
            />

            {/* ✅ NOUVEAU : On attache la réf juste au-dessus du conteneur principal */}
            <div ref={listTopRef} className="professional-management-container bg-light">
                <Container className="py-4">

                    {/* ===== HEADER ===== */}
                    <div className="pm-header rounded-4 p-4 p-md-5 mb-4 position-relative overflow-hidden">
                        {/* Icône décorative : purement visuelle, ne doit jamais gêner le contenu */}
                        <FaUsers className="pm-header-decoration" aria-hidden="true" />

                        <div className="pm-header-content position-relative">
                            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                                <div>
                                    <h1 className="text-white fw-bold pm-header-title mb-2">
                                        👋 Gestion des Professionnels
                                    </h1>
                                    <p className="text-white-50 mb-0">
                                        Gérez et validez les demandes d'inscription des professionnels
                                    </p>
                                </div>
                                <Button
                                    variant="light"
                                    size="sm"
                                    className="pm-refresh-btn rounded-pill px-4 align-self-start align-self-lg-auto"
                                    onClick={fetchProfessionals}
                                >
                                    <FaSync className="me-1" /> Actualiser
                                </Button>
                            </div>

                            {/* Modification 2 : Ajout de la stat "Sans identification" */}
                            <div className="pm-header-stats d-flex flex-wrap gap-3 gap-md-4 mt-3 pt-3 border-top border-white border-opacity-25">
                                <span className="text-white">
                                    <strong className="fs-5">{filterStats.all}</strong> Total
                                </span>
                                <span className="text-white">
                                    <strong className="fs-5">{filterStats.accepted}</strong> Validés
                                </span>
                                <span className="text-white">
                                    <strong className="fs-5">{filterStats.pending}</strong> En attente
                                </span>
                                <span className="text-white">
                                    <strong className="fs-5">{filterStats.rejected}</strong> Rejetés
                                </span>
                                <span className="text-white">
                                    <strong className="fs-5">{filterStats.no_identification}</strong> Sans identification
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ===== RECHERCHE ===== */}
                    <div className="mb-4">
                        <div className="pm-search-bar d-flex align-items-center">
                            <span className="pm-search-icon ps-3 pe-2">
                                <FaSearch />
                            </span>
                            <Form.Control
                                type="text"
                                placeholder="Rechercher un professionnel..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setShowClearSearch(e.target.value.length > 0);
                                }}
                                className="pm-search-input border-0 shadow-none py-3"
                            />
                            {showClearSearch && (
                                <Button
                                    variant="link"
                                    className="text-muted p-2"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setShowClearSearch(false);
                                    }}
                                >
                                    <FaTimes />
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* ===== FILTRES ===== */}
                    <div className="mb-4">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <FaFilter className="text-primary" />
                                <h6 className="mb-0 fw-semibold text-dark">Filtres</h6>
                                <Badge bg="secondary" pill>
                                    {sortedFilteredProfessionals.length} résultat{sortedFilteredProfessionals.length > 1 ? 's' : ''}
                                </Badge>
                            </div>
                            {activeFilter !== 'all' && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="text-primary text-decoration-none p-0"
                                    onClick={() => setActiveFilter('all')}
                                >
                                    ✕ Tout voir
                                </Button>
                            )}
                        </div>

                        <div className="pm-filter-bar d-flex flex-wrap gap-2 p-2 bg-white rounded-4 border">
                            {filters.map(filter => {
                                const count = filterStats[filter.key] || 0;
                                const isActive = activeFilter === filter.key;
                                return (
                                    <button
                                        key={filter.key}
                                        type="button"
                                        className={`pm-filter-chip btn ${isActive ? 'pm-filter-chip-active' : ''}`}
                                        onClick={() => setActiveFilter(filter.key)}
                                    >
                                        {filter.label}
                                        {count > 0 && (
                                            <Badge
                                                bg={isActive ? 'light' : 'secondary'}
                                                text={isActive ? 'dark' : 'white'}
                                                className="ms-1 rounded-pill"
                                            >
                                                {count}
                                            </Badge>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* ===== GRILLE DES PROFESSIONNELS ===== */}
                    {isLoading && !showLoadingSpinner ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-3 text-muted">Chargement des professionnels...</p>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger">
                            <p>{error}</p>
                            <Button
                                onClick={fetchProfessionals}
                                variant="primary"
                                className="rounded-pill px-4"
                            >
                                Réessayer
                            </Button>
                        </div>
                    ) : (
                        <>
                            <Row className="g-4 mt-1">
                                {currentProfessionals.map(professional => (
                                    <Col key={professional.id} xs={12} md={6} xl={4}>
                                        <Card className="professional-card border-0 rounded-4 shadow-sm h-100">
                                            <div
                                                className="professional-photo-frame position-relative overflow-hidden"
                                                style={{ height: `${CARD_IMAGE_HEIGHT}px`, cursor: 'zoom-in' }}
                                                onClick={() => handleOpenPhotoPreview(professional.displayPhotoUrl)}
                                            >
                                                <img
                                                    src={professional.displayPhotoUrl}
                                                    alt={`${professional.user.firstName} ${professional.user.lastName}`}
                                                    className="professional-photo-img"
                                                    onError={(e) => { e.currentTarget.src = DEFAULT_AVATAR; }}
                                                />

                                                {/* Dégradé pour la lisibilité */}
                                                <div className="professional-photo-gradient position-absolute top-0 start-0 w-100 h-100"></div>

                                                {/* Icône loupe */}
                                                <div className="photo-zoom-hint position-absolute top-50 start-50 translate-middle text-white fs-1">
                                                    <FaSearchPlus />
                                                </div>

                                                {/* Badge de statut */}
                                                <div className="position-absolute top-0 end-0 m-3">
                                                    {getStatusBadge(professional)}
                                                </div>

                                                {/* Informations sur l'image */}
                                                <div className="position-absolute bottom-0 start-0 end-0 p-3 text-white">
                                                    <h5 className="text-white fw-bold mb-0 text-shadow">
                                                        {professional.user.firstName} {professional.user.lastName}
                                                    </h5>
                                                    <p className="text-white-50 small mb-0">
                                                        {getServiceLabel(professional.selectedService) || 'Service non spécifié'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Infos de la carte */}
                                            <Card.Body className="d-flex flex-column">
                                                <div className="d-flex flex-column gap-2 mb-3">
                                                    <div className="d-flex align-items-center gap-2 text-secondary small">
                                                        <FaMapMarkerAlt className="text-primary flex-shrink-0" />
                                                        <span>{professional.user.city || 'Non spécifié'}</span>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2 text-secondary small">
                                                        <FaClock className="text-primary flex-shrink-0" />
                                                        <span>{professional.yearsOfExperience || '0'} ans d'expérience</span>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2 text-secondary small">
                                                        <FaLanguage className="text-primary flex-shrink-0" />
                                                        <span>
                                                            {professional.languages
                                                                ? professional.languages.split(',').map(l => l.trim()).join(', ')
                                                                : 'Non spécifié'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="professional-actions d-flex gap-2 mt-auto">
                                                    <Button
                                                        variant="primary"
                                                        className="flex-grow-1 rounded-pill"
                                                        onClick={() => handleViewDetails(professional)}
                                                    >
                                                        <FaEye className="me-1" /> Voir plus
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        className="flex-grow-1 rounded-pill"
                                                        onClick={() => handleDelete(professional.id)}
                                                    >
                                                        <FaTrash className="me-1" /> Supprimer
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* ===== PAGINATION ===== */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-5">
                                    <Pagination className="pagination-custom flex-wrap justify-content-center">
                                        <Pagination.Prev
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        />
                                        {Array.from({ length: totalPages }, (_, i) => (
                                            <Pagination.Item
                                                key={i + 1}
                                                active={i + 1 === currentPage}
                                                onClick={() => setCurrentPage(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        />
                                    </Pagination>
                                </div>
                            )}

                            {/* ===== AUCUN RÉSULTAT ===== */}
                            {sortedFilteredProfessionals.length === 0 && (
                                <div className="text-center py-5">
                                    <FaSearch size={48} className="text-secondary mb-4" />
                                    <h5 className="text-muted mb-2">Aucun professionnel trouvé</h5>
                                    <p className="text-muted">Essayez de modifier vos critères de recherche ou vos filtres</p>
                                    <Button
                                        variant="primary"
                                        className="rounded-pill px-4"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setActiveFilter('all');
                                            setCurrentPage(1);
                                        }}
                                    >
                                        Réinitialiser les filtres
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Container>
            </div>

            {/* Modal de détails */}
            {isModalOpen && selectedProfessional && (
                <ProfessionalDetailModal
                    professional={selectedProfessional}
                    show={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onValidate={handleValidateProfessional}
                    onReject={handleRejectProfessional}
                />
            )}

            {/* Lightbox pour les photos */}
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                plugins={[Zoom]}
                zoom={{ maxZoomPixelRatio: 3 }}
                controller={{ closeOnBackdropClick: true }}
            />
        </>
    );
};

export default ProfessionalManagementPage;